// Hub principal Formation 125 cm³ — 7 heures (passerelle B → A1)
// Landing dédiée : /formation-125
// Cadre réglementaire : annexe 1 de l'arrêté du 17 déc. 2010, version 13 janv. 2025
// Philosophie CODE 2 ROUES : « TU AS LES BASES. MAINTENANT, TON EXPÉRIENCE COMMENCE. »
// 3 modules : Comprendre (2 h) · Maîtriser (2 h) · Anticiper (3 h)

import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, ArrowRight, Bike, Eye, Sparkles, Route as RouteIcon,
  BookOpen, Clock, Home, ShieldCheck, GraduationCap, Wrench, Zap,
} from 'lucide-react';
import useSeo from '../../hooks/useSeo';
import { LightboxProvider, LightboxImage } from '../../components/LightboxGallery';

// Poster général de la formation 7h
const POSTER_125 = 'https://customer-assets-cm19k8pv.emergentagent.net/job_occasions-jallieu/artifacts/jh84sstm_ChatGPT%20Image%2017%20ao%C3%BBt%202026%2C%2018_03_24.png';
const POSTER_125_ALT = 'Poster officiel Code 2 Roues — Formation 125 cm³ 7 heures : du permis B aux deux-roues, apprendre à observer, maîtriser et anticiper. Un parcours en 3 étapes Comprendre · Maîtriser · Anticiper. Titulaires du permis B depuis 2 ans minimum, motocyclette 125 cm³, attestation de fin de formation.';

// Les 3 modules réglementaires — Rouge · Orange · Bleu
const MODULES = [
  {
    id: 'comprendre',
    num: '01',
    duree: '2 h',
    kicker: 'MODULE 1 · COMPRENDRE',
    title: 'Tu changes d\'univers',
    subtitle: 'Comprendre les risques spécifiques du deux-roues',
    desc: "Tu sais déjà conduire une voiture. Ici tu découvres pourquoi le deux-roues transforme ton exposition au risque, ton regard, ton équilibre et tes marges. Douze chapitres pour changer ta façon de voir la route avant de monter sur la machine.",
    icon: BookOpen,
    color: 'red',
    tintFrom: 'from-red-600/15',
    tintVia: 'via-red-500/8',
    accent: '#cf2031',
    href: '/formation-125/comprendre',
    sessions: '12 chapitres · Théorie premium',
  },
  {
    id: 'maitriser',
    num: '02',
    duree: '2 h',
    kicker: 'MODULE 2 · MAÎTRISER',
    title: 'La machine devient ton outil',
    subtitle: 'Prendre le contrôle réel de ta 125',
    desc: "Plateau, hors circulation. Tu découvres la machine, tu construis ton poste de conduite et tu acquiers assez de maîtrise pour ne plus penser aux commandes. Quinze chapitres pour rendre la 125 naturelle sous tes mains.",
    icon: Wrench,
    color: 'orange',
    tintFrom: 'from-orange-500/15',
    tintVia: 'via-orange-500/8',
    accent: '#f59e0b',
    href: '/formation-125/maitriser',
    sessions: '15 chapitres · Hors circulation',
  },
  {
    id: 'anticiper',
    num: '03',
    duree: '3 h',
    kicker: 'MODULE 3 · ANTICIPER',
    title: 'La route devient ton terrain',
    subtitle: 'Voir la route comme un conducteur de deux-roues',
    desc: "Circulation en et hors agglomération. Tu réutilises ton expérience B, mais tu apprends à regarder autrement : marges, positionnement, lecture des autres, dépassements, angles morts, virages, prise de décision. Quinze chapitres pour transformer tes acquis en conduite sûre.",
    icon: RouteIcon,
    color: 'blue',
    tintFrom: 'from-blue-600/15',
    tintVia: 'via-blue-500/8',
    accent: '#3b82f6',
    href: '/formation-125/anticiper',
    sessions: '15 chapitres · Circulation',
  },
];

// Ce qui rend cette formation différente
const DIFFERENCES = [
  {
    icon: Eye,
    kicker: 'Tu sais conduire',
    title: 'Tu n\'apprends pas à conduire',
    desc: "On ne refait pas un cours de Code. Tu es déjà titulaire du permis B : tu connais la route. Notre travail commence là où le tien laisse un vide : le regard, la place du corps, l'exposition au risque en deux-roues.",
  },
  {
    icon: ShieldCheck,
    kicker: 'Ton corps est exposé',
    title: 'Tu changes de posture face au risque',
    desc: "En voiture, une petite erreur peut rester sans conséquence. En 125, ton corps devient directement exposé. Placement, adhérence, équilibre et regard prennent une autre dimension : c'est cette transformation que tu vas apprendre.",
  },
  {
    icon: Zap,
    kicker: '7 heures suffisent-elles ?',
    title: 'Nous ne te promettons pas d\'être motard',
    desc: "En 7 heures, tu ne deviens pas un motard expérimenté. Tu acquiers les bases qui te permettront de commencer à rouler en 125 avec méthode, prudence et conscience de tes limites. Ton expérience commencera après.",
  },
];

// Conditions d'accès
const CONDITIONS = [
  { label: 'PERMIS REQUIS', value: 'Permis B en cours de validité depuis 2 ans minimum', icon: GraduationCap },
  { label: 'FORMATION', value: '7 heures obligatoires (théorie + hors circulation + circulation)', icon: Clock },
  { label: 'PAS D\'EXAMEN', value: 'Attestation de suivi de formation — pas d\'épreuve finale', icon: ShieldCheck },
  { label: 'VÉHICULES AUTORISÉS', value: 'Motos & scooters 125 cm³ (≤ 11 kW · A1)', icon: Bike },
];

export default function Formation125Hub() {
  useSeo({
    title: 'Formation 125 cm³ — 7 heures · CODE 2 ROUES',
    description: "De l'automobiliste au pilote de deux-roues : 7 heures pour changer ta façon de voir la route. 3 modules Comprendre · Maîtriser · Anticiper. Cadre annexe 1 arrêté 17 déc. 2010, version 13 janv. 2025.",
  });

  return (
    <LightboxProvider>
    <div className="min-h-screen bg-background text-foreground pb-24 print:pb-0" data-testid="formation-125-hub">
      {/* Barre haute */}
      <div className="sticky top-0 z-40 border-b border-white/10 bg-background/95 backdrop-blur print:hidden">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3">
          <Button asChild variant="ghost" size="sm" data-testid="btn-back-home">
            <Link to="/" reloadDocument><ArrowLeft className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline"> Retour</span></Link>
          </Button>
          <div className="hidden md:block flex-1 text-center">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-orange-500">
              Formation 125 · Passerelle B → A1
            </div>
          </div>
          <div className="flex-1 md:hidden" />
          <Button asChild variant="outline" size="sm">
            <Link to="/" reloadDocument><Home className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline"> Accueil</span></Link>
          </Button>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-10">
        {/* ============= HERO ============= */}
        <header className="text-center pt-6" data-testid="f125-hero">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-500/15 border border-orange-500/30 text-[10px] font-black uppercase tracking-widest text-orange-400 mb-5">
            <GraduationCap className="w-3 h-3" /> 7 heures · Titulaires du permis B
          </div>
          <div className="text-[11px] font-black uppercase tracking-[0.28em] text-muted-foreground mb-2">
            Formation
          </div>
          <h1
            className="text-4xl sm:text-6xl md:text-7xl font-black leading-none mb-3 tracking-tight bg-gradient-to-b from-white to-white/70 bg-clip-text text-transparent"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            125 cm³
          </h1>
          <p className="text-lg md:text-2xl text-orange-500 font-black tracking-wide mb-6">
            De l'automobiliste au pilote de deux-roues
          </p>

          <div className="max-w-2xl mx-auto p-4 md:p-5 rounded-xl border border-orange-500/30 bg-orange-500/5">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-orange-400 mb-2">
              Fil rouge de la formation
            </div>
            <div
              className="text-sm md:text-base font-black text-foreground"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              JE REGARDE → JE COMPRENDS → JE DÉCIDE → J'AGIS → JE CONTRÔLE
            </div>
          </div>

          <p className="mt-6 text-sm md:text-base text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Tu sais déjà circuler. Maintenant, tu vas apprendre à circuler <strong className="text-foreground">autrement</strong>.
            La formation 125 dure <strong className="text-foreground">7 heures obligatoires</strong> et se compose de 3 modules :
            comprendre les risques spécifiques du deux-roues, maîtriser la machine hors circulation, anticiper la conduite en circulation.
            <br className="hidden md:block" />
            <span className="text-[11px] italic text-muted-foreground/70 block mt-2">
              Cadre : annexe 1 de l'arrêté du 17 décembre 2010, version en vigueur depuis le 13 janvier 2025.
            </span>
          </p>
        </header>

        {/* ============= POSTER OFFICIEL 125 (projetable en salle) ============= */}
        <section data-testid="f125-poster">
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-orange-400 mb-3 text-center">
            Poster officiel · Projetable en salle
          </div>
          <LightboxImage src={POSTER_125} alt={POSTER_125_ALT} caption="Formation 125 cm³ — 7 heures · Poster officiel CODE 2 ROUES" testId="f125-poster-image" />
        </section>

        {/* ============= CE QUI CHANGE ============= */}
        <section data-testid="f125-differences">
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-orange-400 mb-3 text-center">
            Pourquoi cette formation est différente
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            {DIFFERENCES.map((d, i) => {
              const Icon = d.icon;
              return (
                <div
                  key={i}
                  className="p-4 rounded-xl border border-white/10 bg-black/20"
                  data-testid={`f125-difference-${i}`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-orange-500/15 border border-orange-500/30 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-orange-400" />
                    </div>
                    <div className="text-[10px] font-black uppercase tracking-[0.18em] text-orange-500 pt-1.5">
                      {d.kicker}
                    </div>
                  </div>
                  <h3
                    className="text-base font-black leading-tight text-foreground mb-2"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                  >
                    {d.title}
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{d.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* ============= 3 MODULES ============= */}
        <section data-testid="f125-modules">
          <div className="text-center mb-6">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-orange-400 mb-2">
              La formation en 3 modules
            </div>
            <h2
              className="text-2xl md:text-3xl font-black text-foreground tracking-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              7 heures pour changer ta façon de voir la route
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {MODULES.map((m) => {
              const Icon = m.icon;
              const colorClasses = {
                red: {
                  border: 'border-red-500/30 hover:border-red-500/60',
                  bg: 'from-red-600/15 via-red-500/8 hover:from-red-600/25',
                  badge: 'bg-red-500/15 border-red-500/30 text-red-400',
                  numText: 'text-red-500/25',
                  kicker: 'text-red-500',
                  subtitle: 'text-red-400',
                  cta: 'text-red-500',
                },
                orange: {
                  border: 'border-orange-500/30 hover:border-orange-500/60',
                  bg: 'from-orange-500/15 via-orange-500/8 hover:from-orange-500/25',
                  badge: 'bg-orange-500/15 border-orange-500/30 text-orange-400',
                  numText: 'text-orange-500/25',
                  kicker: 'text-orange-500',
                  subtitle: 'text-orange-400',
                  cta: 'text-orange-500',
                },
                blue: {
                  border: 'border-blue-500/30 hover:border-blue-500/60',
                  bg: 'from-blue-600/15 via-blue-500/8 hover:from-blue-600/25',
                  badge: 'bg-blue-500/15 border-blue-500/30 text-blue-400',
                  numText: 'text-blue-500/25',
                  kicker: 'text-blue-500',
                  subtitle: 'text-blue-400',
                  cta: 'text-blue-500',
                },
              }[m.color];

              return (
                <Link
                  key={m.id}
                  to={m.href}
                  reloadDocument
                  className={`group relative overflow-hidden rounded-2xl border bg-gradient-to-br to-transparent p-5 transition-all flex flex-col ${colorClasses.border} ${colorClasses.bg}`}
                  data-testid={`f125-module-${m.id}`}
                  style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                >
                  <div className="pointer-events-none absolute -right-6 -bottom-4 opacity-10 group-hover:opacity-20 transition">
                    <Icon size={130} strokeWidth={1.2} style={{ color: m.accent }} />
                  </div>
                  <div className="relative flex-1 flex flex-col">
                    <div className="flex items-start justify-between mb-3">
                      <span
                        className={`text-5xl font-black leading-none ${colorClasses.numText}`}
                        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                      >
                        {m.num}
                      </span>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[10px] font-black uppercase tracking-widest ${colorClasses.badge}`}
                      >
                        <Clock className="w-3 h-3" /> {m.duree}
                      </span>
                    </div>
                    <div className={`text-[10px] font-black uppercase tracking-[0.18em] mb-2 ${colorClasses.kicker}`}>
                      {m.kicker}
                    </div>
                    <h3
                      className="text-lg font-black leading-tight text-foreground mb-1.5"
                      style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                    >
                      {m.title}
                    </h3>
                    <p className={`text-xs font-bold uppercase tracking-wide mb-3 ${colorClasses.subtitle}`}>{m.subtitle}</p>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4 flex-1">{m.desc}</p>
                    <div className="flex items-center justify-between pt-3 border-t border-white/10">
                      <span className="text-[10px] italic text-muted-foreground/70">{m.sessions}</span>
                      <span className={`inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider group-hover:gap-2.5 transition-all ${colorClasses.cta}`}>
                        Découvrir <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* ============= CONDITIONS ============= */}
        <section
          className="rounded-2xl border border-white/10 bg-black/20 p-5 md:p-6"
          data-testid="f125-conditions"
        >
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-orange-400 mb-4 text-center">
            Conditions pour suivre la formation 125
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {CONDITIONS.map((c, i) => {
              const Icon = c.icon;
              return (
                <div
                  key={i}
                  className="flex items-start gap-3 p-3 rounded-xl border border-white/10 bg-black/20"
                >
                  <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-orange-500/15 border border-orange-500/30 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-orange-400" />
                  </div>
                  <div className="flex-1 pt-0.5">
                    <div className="text-[10px] font-black uppercase tracking-widest text-orange-400 mb-0.5">
                      {c.label}
                    </div>
                    <div className="text-sm text-foreground/90">{c.value}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ============= MESSAGE FINAL ============= */}
        <section
          className="rounded-2xl border-2 border-orange-500/30 bg-gradient-to-br from-orange-500/15 via-orange-500/8 to-transparent p-6 md:p-8 text-center"
          data-testid="f125-final-message"
        >
          <div className="inline-flex items-center gap-1.5 mb-3">
            <Sparkles className="w-4 h-4 text-orange-400" />
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-orange-400">
              La promesse Code 2 Roues
            </div>
          </div>
          <h3
            className="text-xl md:text-3xl font-black text-foreground mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            TU AS LES BASES. MAINTENANT, TON EXPÉRIENCE COMMENCE.
          </h3>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-4">
            Tu connais mieux les risques. Tu maîtrises les fondamentaux de ta machine. Tu sais où regarder.
            Tu sais pourquoi anticiper. Tu connais aussi tes limites. <strong className="text-foreground">Et surtout : tu continues à apprendre après ces 7 heures.</strong>
          </p>
          <p className="text-xs italic text-muted-foreground/70 max-w-2xl mx-auto">
            La formation 125 n'est pas un mini-permis A1. Le permis A1 traditionnel demande 20 h de pratique + épreuves.
            Ici, nous t'apportons en 7 h la méthode, la prudence et la conscience de tes limites.
          </p>
        </section>
      </main>
    </div>
    </LightboxProvider>
  );
}
